import { useState, useMemo, useCallback, useEffect } from "react";
import { TroquinhoCalendar } from "@/components/shared/TroquinhoCalendar";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Plus, Minus, Search, Filter, Calendar, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, Wallet, User, Users, CreditCard, Building2,
  Banknote, QrCode, Receipt, FileText, Pencil, Trash2, X, Check,
  Clock, Tag, UserPlus, Repeat, AlertTriangle, Sparkles, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useOracle } from "@/contexts/OracleContext"; 
import { OracleToggle } from "@/components/shared/OracleToggle";
import { 
  subscribeToTransactions, 
  addTransaction, 
  updateTransaction, 
  deleteTransaction,
  deleteTransactionSeries,
  deleteSeriesFromDate,
  updateTransactionSeries,
  updateSeriesFromDate
} from "@/services/transactionsService";
import { subscribeToConnections, addConnection } from "@/services/connectionsService";

// ==================== CONSTANTS ====================

const INCOME_CATEGORIES = [
  "Salário", "Freelance", "Bônus/13º", "Investimentos", "Vendas",
  "Serviços Prestados", "Comissão", "Reembolso", "Presente/Doação",
  "Pensão/Benefício", "Outros"
];

const EXPENSE_CATEGORIES = [
  "Alimentação", "Moradia", "Transporte", "Saúde", "Educação",
  "Assinaturas", "Delivery", "Cuidados Pessoais", "Comprinhas",
  "Lazer", "Presentes", "Serviço", "Outros"
];

const PAYMENT_METHODS = [
  { id: "credit", label: "Crédito", icon: CreditCard },
  { id: "debit", label: "Débito", icon: Building2 },
  { id: "pix", label: "PIX ou Transf.", icon: QrCode },
  { id: "cash", label: "Dinheiro", icon: Banknote },
  { id: "boleto", label: "Boleto", icon: FileText },
  { id: "other", label: "Outros", icon: Receipt },
];

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

// ==================== HELPER FUNCTIONS ====================

const formatCurrency = (value) => {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getTransactionIcon = (type) => {
  switch (type) {
    case "income": return { icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-100" };
    case "expense": return { icon: TrendingDown, color: "text-rose-600", bg: "bg-rose-100" };
    default: return { icon: Receipt, color: "text-slate-600", bg: "bg-slate-100" };
  }
};

// --- ENTRA ---
const handleCurrencyInputChange = (value, callback) => {
  const cleanValue = value.replace(/\D/g, "");
  const floatValue = parseFloat(cleanValue) / 100;
  if (isNaN(floatValue)) {
    callback("0");
  } else {
    callback(floatValue.toString());
  }
};

// Função para exibir o valor enquanto digita (máscara visual)
const displayMask = (value) => {
  if (!value) return "";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
  }).format(parseFloat(value));
};

// ==================== MAIN COMPONENT ====================

export default function Transactions() {
  const { user } = useAuth();
  
  // Period state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // View mode
  const [viewMode, setViewMode] = useState("individual"); // individual | grupo

  // --- INÍCIO DO CÓDIGO NOVO (HELPER) ---
const getInvoiceSuggestions = (purchaseDateStr, card) => {
  if (!card || !purchaseDateStr) return [];
  const purchaseDate = parseISO(purchaseDateStr);
  const buyDay = purchaseDate.getDate();
  const monthOffset = buyDay >= card.closeDay ? 1 : 0;
  return [0, 1, 2].map(offset => {
    const targetDate = new Date(purchaseDate.getFullYear(), purchaseDate.getMonth() + monthOffset + offset, card.dueDay);
    return {
      date: targetDate.toISOString().split('T')[0],
      label: format(targetDate, "MMM", { locale: ptBR })
    };
  });
};
// --- FIM DO CÓDIGO NOVO (HELPER) ---
  
  // Data state
  const [transactions, setTransactions] = useState([]);
  const [connections, setConnections] = useState({ people: [], cards: [], banks: [], categories: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { isOracleActive } = useOracle();

  // Sincroniza o calendário com o Modo Oráculo
  useEffect(() => {
    const hoje = new Date();
    if (isOracleActive) {
      const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      setCurrentMonth(dataFutura.getMonth());
      setCurrentYear(dataFutura.getFullYear());
    } else {
      setCurrentMonth(hoje.getMonth());
      setCurrentYear(hoje.getFullYear());
    }
  }, [isOracleActive]);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // all | income | expense
  
  // Modal states
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalType, setCategoryModalType] = useState("income");
  const [newCategoryName, setNewCategoryName] = useState("");
  
  // Edit/Delete state
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Series action confirmation state
  const [seriesActionModal, setSeriesActionModal] = useState({
    isOpen: false,
    type: null, // 'edit' | 'delete'
    transaction: null,
    pendingData: null // for edit: the data to apply
  });
  
  // Form state (shared across modals)
  const [form, setForm] = useState({
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    dueDate: "",
    category: "",
    paymentMethod: "",
    cardId: "",
    bankId: "",
    isRecurring: false,
    recurringMonths: "",
    isDivided: false,
    allocateToOthers: false,
    divisionType: "equal", // equal | manual
    selectedParticipants: [],
    participantShares: {},
  });

  // Subscribe to transactions from Firestore
  useEffect(() => {
    if (!user?.uid) return;
    setTransactions([]); // <--- ADICIONE ESTA LINHA para resetar a lista ao trocar de mês
    setIsLoading(true);
  
    
    // Só mostramos o loading se ainda não tivermos transações (primeira carga)
    if (transactions.length === 0) setIsLoading(true);

    const unsubscribeTransactions = subscribeToTransactions(
      user.uid, 
      currentMonth, 
      currentYear, 
      (data) => {
        setTransactions(data);
        setIsLoading(false);
      },
      (error) => {
        setIsLoading(false);
        // Error silenced for production
      }
    );

    const unsubscribeConnections = subscribeToConnections(user.uid, (data) => {
      setConnections(data);
    });

    return () => {
      unsubscribeTransactions();
      unsubscribeConnections();
    };
  }, [user?.uid, currentMonth, currentYear]); // Usar user?.uid evita loops se o objeto user mudar
  

  // Calculate summary based on view mode - filtered by current period
  const summary = useMemo(() => {
  const periodTransactions = transactions.filter(t => {
    // ALTERAÇÃO: Usamos prioritariamente o dueDate para o cálculo do saldo do mês
    const tDate = parseISO(t.dueDate || t.date);
    return tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear;
  });
    
    const income = periodTransactions
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + (viewMode === "individual" ? t.myShare : t.amount), 0);
    
    const expenses = periodTransactions
  .filter(t => t.type === "expense") // Agora o 'te_pago_depois' não é mais subtraído do saldo
  .reduce((sum, t) => sum + (viewMode === "individual" ? t.myShare : t.amount), 0);
    
    return { income, expenses, balance: income - expenses };
  }, [transactions, viewMode, currentMonth, currentYear]);

  // Filter transactions - includes period filter
  const filteredTransactions = useMemo(() => {
    // First filter by current month/year
    let filtered = transactions.filter(t => {
      const tDate = t.dueDate ? parseISO(t.dueDate) : parseISO(t.date);
      return tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear;
    });
    
    // Then apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Then apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(t => t.type === filterType);
    }
    
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transactions, searchQuery, filterType, currentMonth, currentYear]);

  // Verifica se o usuário não tem nenhuma conexão básica para começar
  const isFirstTime = useMemo(() => {
    return connections.banks.length === 0 && 
           connections.cards.length === 0 && 
           connections.categories.length === 0;
  }, [connections]);

  // Reset form
  const resetForm = useCallback(() => {
    setForm({
      description: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      dueDate: "",
      category: "",
      paymentMethod: "",
      cardId: "",
      bankId: "",
      isRecurring: false,
      recurringMonths: "",
      isDivided: false,
      allocateToOthers: false,
      divisionType: "equal",
      selectedParticipants: [],
      participantShares: {},
      showManualDue: false,
    });
    setEditingTransaction(null);
  }, []);

  // --- NOVA LÓGICA: Escuta a Home e abre o modal ---
  const location = useLocation();
  const navigate = useNavigate();

 useEffect(() => {
    const modalType = location.state?.openModal;
    if (modalType) {
      resetForm();
      if (modalType === 'income') setShowIncomeModal(true);
      else if (modalType === 'expense') setShowExpenseModal(true);
      
      // Limpa o estado da rota para não reabrir ao atualizar a página
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, resetForm]);
  

  // Helper function to add months to a date
   const addMonthsToDate = (dateString, months) => {
    if (!dateString) return dateString;
    const [y, m, d] = dateString.slice(0, 10).split("-").map(Number);
    const target = new Date(y, m - 1 + months, 1);
    // Se o dia não existir no mês (ex: 31 em fevereiro), usa o último dia do mês
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(d, lastDay));
    const mm = String(target.getMonth() + 1).padStart(2, "0");
    const dd = String(target.getDate()).padStart(2, "0");
    return `${target.getFullYear()}-${mm}-${dd}`;
  };

  // Helper function to create recurring transactions
  const createRecurringTransactions = async (baseData, userId, startAt = 0) => {
    const recurringMonths = parseInt(baseData.recurringMonths) || 1;
    const seriesId = baseData.seriesId || Date.now().toString(); //Usa o ID existente ou cria um novo
    
    const promises = [];
    
    for (let i = startAt; i < recurringMonths; i++) {
      const transactionData = {
        ...baseData,
        date: addMonthsToDate(baseData.date, i),
        dueDate: addMonthsToDate(baseData.dueDate, i),
        seriesId: seriesId,
        seriesIndex: i + 1,
        seriesTotalMonths: recurringMonths,
        originalDate: baseData.date, // data da compra original (parcela 1)
      };
      
      promises.push(addTransaction(userId, transactionData));
    }
    
    await Promise.all(promises);
    return seriesId; // Agora ele retorna o ID correto para vincular os lançamentos
  };

  // Open edit modal
  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setForm({
      description: transaction.description,
      amount: transaction.amount.toString(),
      date: transaction.date,
      dueDate: transaction.dueDate || "",
      category: transaction.category,
      paymentMethod: transaction.paymentMethod || "",
      cardId: transaction.cardId || "",
      bankId: transaction.bankId || "",
      isRecurring: transaction.isRecurring || false,
      recurringMonths: transaction.recurringMonths?.toString() || "",
      isDivided: transaction.isDivided || false,
      // Se a minha parte for 0 e estiver dividido, então é 100% para os outros
      allocateToOthers: transaction.isDivided && transaction.myShare === 0,
      divisionType: "equal",
      selectedParticipants: transaction.participants?.map(p => p.id) || [],
      participantShares: transaction.participants?.reduce((acc, p) => ({ ...acc, [p.id]: p.share.toString() }), {}) || {},
    });
    
    if (transaction.type === "income") setShowIncomeModal(true);
    else if (transaction.type === "expense") setShowExpenseModal(true);
  };

  // Calculate division
  const calculateDivision = useCallback(() => {
    const amount = parseFloat(form.amount) || 0;
    const participants = form.selectedParticipants;
    
    if (form.divisionType === "equal") {
      const totalParticipants = form.allocateToOthers ? participants.length : participants.length + 1;
      const shareEach = amount / totalParticipants;
      const shares = {};
      participants.forEach(pId => {
        shares[pId] = shareEach;
      });
      return { shares, myShare: form.allocateToOthers ? 0 : shareEach };
    }
    
    // Manual division
    const shares = {};
    let totalOthers = 0;
    participants.forEach(pId => {
      const share = parseFloat(form.participantShares[pId]) || 0;
      shares[pId] = share;
      totalOthers += share;
    });
    return { shares, myShare: amount - totalOthers };
  }, [form]);

  // Save income
  const handleSaveIncome = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error("Valor inválido!");
      return;
    }
    if (!form.category) {
      toast.error("Selecione uma categoria!");
      return;
    }
    if (!form.dueDate) {
      toast.error("Informe a data do recebimento!");
      return;
    }

    setIsSaving(true);
    const amount = parseFloat(form.amount);
    const division = form.isDivided ? calculateDivision() : { shares: {}, myShare: amount };

    const transactionData = {
      type: "income",
      description: form.description || form.category,
      amount,
      date: form.date,
      dueDate: form.dueDate,
      category: form.category,
      paymentMethod: form.paymentMethod,
      cardId: form.cardId || null,
      bankId: form.bankId || null,
      isRecurring: form.isRecurring,
      recurringMonths: form.isRecurring ? parseInt(form.recurringMonths) || 1 : null,
      isDivided: form.isDivided,
      participants: form.selectedParticipants.map(pId => {
        const person = connections.people.find(p => p.id === pId);
        return { id: pId, name: person?.name, share: division.shares[pId] || 0 };
      }),
      myShare: division.myShare,
    };

    try {
if (editingTransaction) {
        if (editingTransaction.seriesId) {
          setSeriesActionModal({ isOpen: true, type: 'edit', transaction: editingTransaction, pendingData: transactionData });
          setIsSaving(false);
          return;
        }
        await updateTransaction(editingTransaction.id, transactionData);
        toast.success("Receita atualizada!");
      }
      else {
        // Para novos lançamentos
        if (form.isRecurring && parseInt(form.recurringMonths) > 1) {
          await createRecurringTransactions(transactionData, user.uid);
          toast.success(`Série de ${parseInt(form.recurringMonths)} receitas criada! 💰`);
        } else {
          await addTransaction(user.uid, transactionData);
          toast.success("Receita registrada! 💰");
        }
      }
      setShowIncomeModal(false);
      resetForm();
    } catch (error) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setIsSaving(false);
  };

  // Save expense
  const handleSaveExpense = async () => {
  if (!form.amount || parseFloat(form.amount) <= 0) {
    toast.error("Valor inválido!");
    return;
  }
  if (!form.category) {
    toast.error("Selecione uma categoria!");
    return;
  }
  
 if (form.paymentMethod === 'credit' && !form.dueDate) {
    toast.error("Por favor, selecione em qual fatura lançar!");
    return;
  }

  setIsSaving(true);
  const amount = parseFloat(form.amount);
  const division = form.isDivided ? calculateDivision() : { shares: {}, myShare: amount };

  // LÓGICA ARQUITETURAL: Garantir que dueDate nunca seja nulo
  const finalDueDate = form.dueDate || form.date;

  const transactionData = {
    type: "expense",
    description: form.description || form.category,
    amount,
    date: form.date,
    dueDate: finalDueDate, // O campo mestre para a filtragem
    category: form.category,
    paymentMethod: form.paymentMethod,
    cardId: form.cardId || null,
    bankId: form.bankId || null,
    isRecurring: form.isRecurring,
    recurringMonths: form.isRecurring ? parseInt(form.recurringMonths) || 1 : null,
    isDivided: form.isDivided,
    participants: form.selectedParticipants.map(pId => {
      const person = connections.people.find(p => p.id === pId);
      return { id: pId, name: person?.name, share: division.shares[pId] || 0 };
    }),
    myShare: division.myShare,
  };

  try {
    if (editingTransaction) {
      if (editingTransaction.seriesId) {
        setSeriesActionModal({ isOpen: true, type: 'edit', transaction: editingTransaction, pendingData: transactionData });
        setIsSaving(false);
        return;
      }
      await updateTransaction(editingTransaction.id, transactionData);
      toast.success("Despesa atualizada!");
    } else {
      if (form.isRecurring && parseInt(form.recurringMonths) > 1) {
        await createRecurringTransactions(transactionData, user.uid);
        toast.success(`Série de despesas criada! 📝`);
      } else {
        await addTransaction(user.uid, transactionData);
        
        // FEEDBACK END-TO-END (Usando date-fns e o padrão de navegação do seu componente)
        const targetDate = parseISO(finalDueDate);
        if (targetDate.getMonth() !== currentMonth || targetDate.getFullYear() !== currentYear) {
          toast.success(`Lançado na fatura de ${format(targetDate, "MMMM", { locale: ptBR })}`, {
            description: "Clique abaixo para navegar até o mês.",
            action: {
              label: "Ver mês",
              onClick: () => {
                setCurrentMonth(targetDate.getMonth());
                setCurrentYear(targetDate.getFullYear());
              }
            }
          });
        } else {
          toast.success("Despesa registrada! 📝");
        }
      }
    }
    setShowExpenseModal(false);
    resetForm();
  } catch (error) {
    toast.error("Erro ao salvar. Tente novamente.");
  }
  setIsSaving(false);
};

  // Delete transaction
  const handleDeleteClick = (transaction) => {
    if (transaction.seriesId) {
      // It's a series transaction, open the series action modal
      setSeriesActionModal({
        isOpen: true,
        type: 'delete',
        transaction: transaction,
        pendingData: null
      });
    } else {
      // Regular transaction, show simple confirmation
      setDeleteConfirm(transaction);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setIsSaving(true);
    try {
      await deleteTransaction(deleteConfirm.id);
      toast.success("Lançamento excluído!");
    } catch (error) {
      toast.error("Erro ao excluir. Tente novamente.");
    }
    setIsSaving(false);
    setDeleteConfirm(null);
  };

  // Handle series action (edit or delete with options)
  const handleSeriesAction = async (action) => {
    // action: 'only_this' | 'this_and_future' | 'all'
    const { type, transaction, pendingData } = seriesActionModal;
    
    if (!transaction) return;
    
    setIsSaving(true);
    
    try {
      if (type === 'delete') {
        if (action === 'only_this') {
          await deleteTransaction(transaction.id);
          toast.success("Lançamento excluído!");
        } else if (action === 'this_and_future') {
          const count = await deleteSeriesFromDate(user.uid, transaction.seriesId, transaction.date);
          toast.success(`${count} lançamento${count > 1 ? 's' : ''} excluído${count > 1 ? 's' : ''}!`);
        } else if (action === 'all') {
          const count = await deleteTransactionSeries(user.uid, transaction.seriesId);
          toast.success(`Série completa excluída (${count} lançamentos)!`);
        }
      } else if (type === 'edit' && pendingData) {
        // Extract only the fields that should be synced across the series
        const syncData = {
          description: pendingData.description,
          amount: pendingData.amount,
          category: pendingData.category,
          paymentMethod: pendingData.paymentMethod,
          cardId: pendingData.cardId,
          bankId: pendingData.bankId,
          isDivided: pendingData.isDivided,
          participants: pendingData.participants,
          myShare: pendingData.myShare,
        };
        
        if (action === 'only_this') {
          await updateTransaction(transaction.id, pendingData);
          toast.success("Lançamento atualizado!");
        } else if (action === 'this_and_future') {
          const count = await updateSeriesFromDate(user.uid, transaction.seriesId, transaction.date, syncData);
          toast.success(`${count} lançamento${count > 1 ? 's' : ''} atualizado${count > 1 ? 's' : ''}!`);
        } else if (action === 'all') {
          const count = await updateTransactionSeries(user.uid, transaction.seriesId, syncData);
          toast.success(`Série completa atualizada (${count} lançamentos)!`);
        }
        
        // Close the form modal
        if (transaction.type === 'income') {
          setShowIncomeModal(false);
        } else if (transaction.type === 'expense') {
          setShowExpenseModal(false);
        }
        resetForm();
      }
    } catch (error) {
      toast.error("Erro ao processar. Tente novamente.");
    }
    
    setIsSaving(false);
    setSeriesActionModal({ isOpen: false, type: null, transaction: null, pendingData: null });
  };

  // Add custom category
  const handleAddCategory = async () => {
  if (!newCategoryName.trim()) {
    toast.error("Digite o nome da categoria!");
    return;
  }
  
  setIsSaving(true);
  try {
    // Salva permanentemente no banco de dados como uma "conexão"
    await addConnection(user.uid, {
      name: newCategoryName.trim(),
      type: "category",
      categoryType: categoryModalType // 'income' ou 'expense'
    });
    
    setForm(prev => ({ ...prev, category: newCategoryName.trim() }));
    setNewCategoryName("");
    setShowCategoryModal(false);
    toast.success("Categoria salva!");
  } catch (error) {
    toast.error("Erro ao salvar categoria.");
  }
  setIsSaving(false);
};

  // Get all categories
const getAllCategories = (type) => {
  const base = type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  
  // Pegamos as categorias do objeto connections. 
  // O "|| []" garante que se não houver nenhuma, o código não dê erro.
  const custom = (connections?.categories || [])
    .filter(cat => cat.categoryType === type)
    .map(cat => cat.name);
    
  return [...base, ...custom];
};

  return (
    <div className="min-h-screen bg-[#fafbfc] pb-24">
      <div className="max-w-lg mx-auto px-5 py-6">
        
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-1">
            Lançamentos
          </h1>
          <p className="text-slate-500 text-sm">
            Gerencie suas receitas e despesas
          </p>
        </motion.header>

        {/* Period Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex items-center justify-between mb-5"
        >

          {/* Navegador de Período Padronizado */}
          <div className="flex-1 max-w-[200px]">
            <TroquinhoCalendar 
              currentMonth={currentMonth} 
              currentYear={currentYear} 
              onPeriodChange={(month, year) => {
                setCurrentMonth(month);
                setCurrentYear(year);
              }} 
            />
          </div>

{/* View Mode Toggle Padronizado */}
          <div className="flex items-center bg-white rounded-full p-1 shadow-sm border border-slate-100 flex-shrink-0 h-12">
            <button
              onClick={() => setViewMode("individual")}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                viewMode === "individual"
                  ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                  : "text-slate-400 hover:bg-slate-50"
              }`}
            >
              <User className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grupo")}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                viewMode === "grupo"
                  ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                  : "text-slate-400 hover:bg-slate-50"
              }`}
            >
              <Users className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

{/* Summary Cards - Layout Otimizado para Mobile */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.1 }}
  className={`grid grid-cols-2 gap-3 mb-6 p-1 rounded-[24px] transition-all duration-500 ${
    isOracleActive ? "bg-purple-600/10 ring-1 ring-purple-500/30 shadow-lg shadow-purple-500/5" : ""
  }`}
>
  {/* Entradas */}
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col items-start">
    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center mb-2 shadow-sm">
      <TrendingUp className="w-4 h-4 text-emerald-600" />
    </div>
    <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Entradas</p>
    <p className="text-base font-black text-emerald-600 truncate w-full">
      R$ {formatCurrency(summary.income)}
    </p>
  </div>
  
  {/* Saídas */}
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col items-start">
    <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center mb-2 shadow-sm">
      <TrendingDown className="w-4 h-4 text-rose-600" />
    </div>
    <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Saídas</p>
    <p className="text-base font-black text-rose-600 truncate w-full">
      R$ {formatCurrency(summary.expenses)}
    </p>
  </div>
  
  {/* Saldo - Ocupando a largura total */}
  <div className="col-span-2 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shadow-sm">
        <Wallet className="w-5 h-5 text-indigo-600" />
      </div>
      <div>
        <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Saldo Atual</p>
        <p className={`text-xl font-black ${summary.balance >= 0 ? "text-indigo-600" : "text-rose-600"}`}>
          R$ {formatCurrency(summary.balance)}
        </p>
      </div>
    </div>
    {isOracleActive && (
      <Badge className="bg-purple-600 text-white border-none text-[10px] animate-pulse">Previsão</Badge>
    )}
  </div>
</motion.div>

        {/* Search & Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex gap-3 mb-6"
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar lançamentos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl bg-white border-slate-200"
              data-testid="search-transactions"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-auto h-12 rounded-xl bg-white border-slate-200" data-testid="filter-type">
              <Filter className="w-5 h-5 text-slate-500" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="income">Receitas</SelectItem>
              <SelectItem value="expense">Despesas</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>
        {/* Transactions List */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            <p className="text-slate-400 text-sm animate-pulse">Consultando os astros financeiros...</p>
          </div>
     ) : filteredTransactions.length === 0 ? (
          isFirstTime ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-indigo-600 rounded-[32px] p-8 text-center text-white shadow-xl shadow-indigo-200 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Sparkles className="w-24 h-24" />
              </div>
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                <Wallet className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-black mb-3">Tudo pronto para começar?</h3>
              <p className="text-indigo-100 text-sm mb-8 leading-relaxed">
                Antes de lançar sua primeira despesa, precisamos configurar seus <b>Bancos</b> ou <b>Cartões</b>. É rápido, prometo!
              </p>
              <Button 
                onClick={() => navigate("/connections")}
                className="w-full h-14 bg-white text-indigo-600 hover:bg-indigo-50 font-black rounded-2xl shadow-lg border-0"
              >
                Configurar Conexões
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="font-bold text-slate-700 mb-2">Nenhum lançamento</h3>
              <p className="text-slate-500 text-sm">
                Use os botões abaixo para adicionar
              </p>
            </motion.div>
          )
        ) : (
          <div className="space-y-3">
              {filteredTransactions.map((transaction, index) => {
                const { icon: Icon, color, bg } = getTransactionIcon(transaction.type);
                const displayAmount = viewMode === "individual" ? transaction.myShare : transaction.amount;
                
                return (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.03 }}
                    className="bg-white rounded-[20px] p-4 border border-slate-200 shadow-sm"
                    data-testid={`transaction-${transaction.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${color}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{transaction.description}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-600">
                              <Tag className="w-3 h-3 mr-1" />
                              {transaction.category}
                            </Badge>
                            {transaction.isRecurring && (
                              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700">
                                <Repeat className="w-3 h-3 mr-1" />
                                Recorrente
                              </Badge>
                            )}
                            {transaction.isDivided && (
                              <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-700">
                                <Users className="w-3 h-3 mr-1" />
                                {transaction.participants?.length || 0}
                              </Badge>
                            )}
                            {transaction.cardId && (
                              <Badge variant="secondary" className="text-[10px] bg-indigo-100 text-indigo-700">
                                <CreditCard className="w-3 h-3 mr-1" />
                                {connections.cards.find(c => c.id === transaction.cardId)?.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className={`font-black text-lg ${
                          transaction.type === "income" ? "text-emerald-600" : "text-rose-600"
                        }`}>
                          {transaction.type === "income" ? "+" : "-"} R$ {formatCurrency(displayAmount)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {format(parseISO(transaction.date), "dd/MM", { locale: ptBR })}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-slate-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(transaction)}
                        className="h-8 px-3 text-slate-600 hover:text-indigo-600"
                        data-testid={`edit-${transaction.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(transaction)}
                        className="h-8 px-3 text-slate-600 hover:text-rose-600"
                        data-testid={`delete-${transaction.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        )}
      </div>

      {/* Ações Rápidas (Agrupadas, menores e limpas) */}
      <div className="fixed bottom-24 right-5 z-30 flex flex-col items-end gap-3">


        {/* Botões de Despesa e Receita (Lado a lado e menores) */}
        <div className="flex gap-3">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            <Button
              onClick={() => { resetForm(); setShowExpenseModal(true); }}
              className="w-12 h-12 rounded-full bg-rose-500 hover:bg-rose-600 shadow-lg shadow-rose-500/20"
              data-testid="fab-expense"
            >
              <Minus className="w-5 h-5 text-white" />
            </Button>
          </motion.div>

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              onClick={() => { resetForm(); setShowIncomeModal(true); }}
              className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 shadow-lg shadow-emerald-500/20"
              data-testid="fab-income"
            >
              <Plus className="w-5 h-5 text-white" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* ==================== INCOME MODAL ==================== */}
      <Dialog open={showIncomeModal} onOpenChange={(open) => { setShowIncomeModal(open); if (!open) resetForm(); }}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] sm:max-w-md sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[24px] border-0 sm:border overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              {editingTransaction ? "Editar Receita" : "Nova Receita"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Description */}
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                placeholder="Ex: Salário, Freelance..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-xl h-12"
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Valor *</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">R$</span>
                <Input
                  type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={displayMask(form.amount)}
                    onChange={(e) => handleCurrencyInputChange(e.target.value, (val) => setForm({ ...form, amount: val }))}
                    className="rounded-xl h-12 pl-12 text-lg font-bold"
                    data-testid="income-amount"
                  />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Data do Lançamento</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="rounded-xl h-12"
                />
              </div>
              <div className="space-y-2">
                <Label>Data do Recebimento *</Label>
                <Input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="income-due-date"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-2">
              Se a receita for recorrente, ela aparecerá a partir deste mês, sempre nesta data.
            </p>

            {/* Category */}
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <div className="flex gap-2">
                <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                  <SelectTrigger className="flex-1 rounded-xl h-12" data-testid="income-category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAllCategories("income").map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 rounded-xl"
                  onClick={() => { setCategoryModalType("income"); setShowCategoryModal(true); }}
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Forma de Recebimento</Label>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_METHODS.map(method => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setForm({ ...form, paymentMethod: method.id, cardId: "", bankId: "" })}
                    className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                      form.paymentMethod === method.id
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <method.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional: Card or Bank */}
            {form.paymentMethod === "credit" && (
              <div className="space-y-2">
                <Label>Cartão</Label>
                <Select value={form.cardId} onValueChange={(value) => setForm({ ...form, cardId: value })}>
                  <SelectTrigger className="rounded-xl h-12">
                    <SelectValue placeholder="Selecione o cartão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.cards.map(card => (
                      <SelectItem key={card.id} value={card.id}>
                        {card.name} •••• {card.lastDigits}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(form.paymentMethod === "debit" || form.paymentMethod === "pix") && (
              <div className="space-y-2">
                <Label>Banco</Label>
                <Select value={form.bankId} onValueChange={(value) => setForm({ ...form, bankId: value })}>
                  <SelectTrigger className="rounded-xl h-12">
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.banks.map(bank => (
                      <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recurring */}
            <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Repeat className="w-5 h-5 text-slate-500" />
                <span className="font-medium text-slate-700">Receita Recorrente</span>
              </div>
              <Checkbox
                checked={form.isRecurring}
                disabled={!!editingTransaction} 
                onCheckedChange={(checked) => setForm({ ...form, isRecurring: checked })}
              />
            </div>
            {form.isRecurring && (
              <div className="space-y-2">
                <Label>Por quantos meses?</Label>
                <Input
                  type="number"
                  inputMode="numeric" // Adicionado para forçar teclado numérico inteiro
                  placeholder="12"
                  value={form.recurringMonths}
                  disabled={!!editingTransaction}
                  onChange={(e) => setForm({ ...form, recurringMonths: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="income-recurring-months"
                />
              </div>
            )}

            {/* Divide */}
            <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-500" />
                <span className="font-medium text-slate-700">Compartilhar o Aporte</span>
              </div>
              <Checkbox
                checked={form.isDivided}
                onCheckedChange={(checked) => setForm({ ...form, isDivided: checked })}
              />
            </div>
            {form.isDivided && (
              <DivisionSection
                form={form}
                setForm={setForm}
                connections={connections}
                type="income"
              />
            )}

           <Button
              onClick={handleSaveIncome}
              disabled={isSaving}
              className="w-full h-12 rounded-xl font-bold bg-emerald-500 hover:bg-emerald-600"
              data-testid="save-income"
            >
              {isSaving ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Salvando...</span>
                </div>
              ) : (
                editingTransaction ? "Salvar Alterações" : "Registrar Receita"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== EXPENSE MODAL ==================== */}
      <Dialog open={showExpenseModal} onOpenChange={(open) => { setShowExpenseModal(open); if (!open) resetForm(); }}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] sm:max-w-md sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[24px] border-0 sm:border overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-rose-600" />
              </div>
              {editingTransaction ? "Editar Despesa" : "Nova Despesa"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* 1. Descrição */}
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                placeholder="Ex: Aluguel, Mercado..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-xl h-12"
              />
            </div>

            {/* 2. Valor */}
            <div className="space-y-2">
              <Label>Valor *</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">R$</span>
                <Input
                  type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={displayMask(form.amount)}
                    onChange={(e) => handleCurrencyInputChange(e.target.value, (val) => setForm({ ...form, amount: val }))}
                    className="rounded-xl h-12 pl-12 text-lg font-bold"
                    data-testid="income-amount"
                  />
              </div>
            </div>

            {/* 3. Meio de Pagamento (SUBIU PARA DIMINUIR FRICÇÃO) */}
            <div className="space-y-2">
              <Label>Meio de Pagamento</Label>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_METHODS.map(method => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setForm({ ...form, paymentMethod: method.id, cardId: "", bankId: "" })}
                    className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                      form.paymentMethod === method.id
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <method.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 4. Condicional de Cartão e Lógica de Faturas */}
            {form.paymentMethod === "credit" && (
              <div className="space-y-4 p-3 bg-slate-50 rounded-2xl border border-slate-200 animate-in fade-in slide-in-from-top-2">
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-indigo-700">Qual cartão?</Label>
                  <Select value={form.cardId} onValueChange={(v) => setForm({ ...form, cardId: v })}>
                    <SelectTrigger className="h-10 bg-white border-rose-200"><SelectValue placeholder="Selecione o cartão" /></SelectTrigger>
                    <SelectContent>
                      {connections.cards.length > 0 ? (
                        connections.cards.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                      ) : (
                        <div className="p-4 text-center">
                          <p className="text-xs text-slate-500 mb-2">Nenhum cartão cadastrado.</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8 text-[10px] border-indigo-200 text-indigo-600"
                            onClick={() => navigate("/connections")}
                          >
                            Ir para Conexões
                          </Button>
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {form.cardId && (
                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase text-indigo-600 font-black">Lançar na fatura de:</Label>
                    <div className="flex gap-2">
                      {getInvoiceSuggestions(form.date, connections.cards.find(c => c.id === form.cardId)).map((inv) => (
                        <button
                          key={inv.date}
                          type="button"
                          onClick={() => setForm({ ...form, dueDate: inv.date, showManualDue: false })}
                          className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                            form.dueDate === inv.date && !form.showManualDue
                            ? "bg-indigo-500 text-white shadow-md" 
                            : "bg-white text-slate-600 border border-slate-200"
                          }`}
                        >
                          {inv.label}
                        </button>
                      ))}
                    </div>
                    <button 
                      type="button"
                      onClick={() => setForm({ ...form, showManualDue: !form.showManualDue })}
                      className="text-[10px] text-slate-500 font-medium underline underline-offset-2"
                    >
                      {form.showManualDue ? "✕ Usar faturas automáticas" : "✎ Escolher outro mês..."}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 5. Se for Débito ou PIX, escolhe o Banco */}
            {(form.paymentMethod === "debit" || form.paymentMethod === "pix") && (
              <div className="space-y-2 animate-in fade-in">
                <Label>Banco</Label>
                <Select value={form.bankId} onValueChange={(v) => setForm({ ...form, bankId: v })}>
                  <SelectTrigger className="rounded-xl h-12"><SelectValue placeholder="Selecione o banco" /></SelectTrigger>
                  <SelectContent>
                        {connections.banks.length > 0 ? (
                          connections.banks.map(bank => <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>)
                        ) : (
                          <div className="p-4 text-center">
                            <p className="text-xs text-slate-500 mb-2">Nenhum banco cadastrado.</p>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-8 text-[10px] border-indigo-200 text-indigo-600"
                              onClick={() => navigate("/connections")}
                            >
                              Ir para Conexões
                            </Button>
                          </div>
                        )}
                      </SelectContent>
                </Select>
              </div>
            )}

            {/* 6. Datas (Compra e Vencimento) */}
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label>Data da Compra</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="rounded-xl h-12"
                />
              </div>

              {(form.paymentMethod !== "credit" || form.showManualDue) && (
                <div className="space-y-2 animate-in slide-in-from-left-2">
                  <Label>{form.paymentMethod === 'credit' ? "Vencimento Manual" : "Data de Vencimento"}</Label>
                  <Input
                    type="date"
                    value={form.dueDate}
                    onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                    className="rounded-xl h-12 bg-slate-50 border-slate-200"
                  />
                </div>
              )}
            </div>

            {/* 7. Categoria */}
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <div className="flex gap-2">
                <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                  <SelectTrigger className="flex-1 rounded-xl h-12" data-testid="expense-category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAllCategories("expense").map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 rounded-xl"
                  onClick={() => { setCategoryModalType("expense"); setShowCategoryModal(true); }}
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Seção de Recorrência */}
            <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
            <Repeat className="w-5 h-5 text-slate-500" />
             <span className="font-medium text-slate-700">Repetir Mensalmente</span>
           </div>
         <Checkbox
            checked={form.isRecurring}
            disabled={!!editingTransaction} 
            onCheckedChange={(checked) => setForm({ ...form, isRecurring: checked })}
            data-testid="recurring-checkbox"
                />
              </div>
            {form.isRecurring && (
              <>
                <div className="space-y-2">
                  <Label>Por quantos meses?</Label>
                  <Input
                    type="number"
                    inputMode="numeric" // Adicionado para forçar teclado numérico inteiro
                    placeholder="12"
                    value={form.recurringMonths}
                    disabled={!!editingTransaction}
                    onChange={(e) => setForm({ ...form, recurringMonths: e.target.value })}
                    className="rounded-xl h-12"
                    data-testid="recurring-months"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  A conta passa a aparecer a partir desse mês, sempre nessa data.
                </p>
              </>
            )}

            {/* Divide */}
            <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-500" />
                <span className="font-medium text-slate-700">Compartilhar despesa</span>
              </div>
              <Checkbox
                checked={form.isDivided}
                onCheckedChange={(checked) => setForm({ ...form, isDivided: checked })}
              />
            </div>
            {form.isDivided && (
              <DivisionSection
                form={form}
                setForm={setForm}
                connections={connections}
                type="expense"
              />
            )}

            <Button
                  onClick={handleSaveExpense}
                  disabled={isSaving} // <--- ISSO TRAVA O BOTÃO
                  className="w-full h-12 rounded-xl font-bold bg-rose-500 hover:bg-rose-600"
                  data-testid="save-expense"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    editingTransaction ? "Salvar Alterações" : "Registrar Despesa"
                  )}
</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* ==================== CATEGORY MODAL ==================== */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="rounded-[24px] mx-4 max-w-sm">
          <DialogHeader>
            <DialogTitle>Nova Categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="Nome da categoria"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="rounded-xl h-12"
            />
            <Button
              onClick={handleAddCategory}
              className="w-full h-12 rounded-xl font-bold bg-indigo-500 hover:bg-indigo-600"
            >
              Criar Categoria
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== DELETE CONFIRMATION (for non-series) ==================== */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-[24px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lançamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{deleteConfirm?.description}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete()}
              className="rounded-xl bg-rose-500 hover:bg-rose-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ==================== SERIES ACTION MODAL (Edit/Delete with options) ==================== */}
      <Dialog 
        open={seriesActionModal.isOpen} 
        onOpenChange={(open) => !open && setSeriesActionModal({ isOpen: false, type: null, transaction: null, pendingData: null })}
      >
        <DialogContent className="rounded-[24px] mx-4 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-indigo-500" />
              {seriesActionModal.type === 'delete' ? 'Excluir da série' : 'Editar série'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-600 text-sm mb-4">
              Essa mudança viaja no tempo? Escolha se quer {seriesActionModal.type === 'delete' ? 'excluir' : 'alterar'} só esse lançamento, daqui pra frente ou a história toda.
            </p>
            
            <div className="space-y-3">
              {/* Only this */}
              <Button
                variant="outline"
                className="w-full h-14 rounded-xl justify-start px-4 hover:border-amber-400 hover:bg-amber-50"
                onClick={() => handleSeriesAction('only_this')}
                disabled={isSaving}
                data-testid="series-action-only-this"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mr-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Apenas este</p>
                  <p className="text-xs text-slate-500">
                    {seriesActionModal.type === 'delete' ? 'Exclui só esse lançamento' : 'Altera só esse lançamento'}
                  </p>
                </div>
              </Button>

              {/* This and future */}
              <Button
                variant="outline"
                className="w-full h-14 rounded-xl justify-start px-4 hover:border-indigo-400 hover:bg-indigo-50"
                onClick={() => handleSeriesAction('this_and_future')}
                disabled={isSaving}
                data-testid="series-action-this-and-future"
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center mr-3">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Este e os próximos</p>
                  <p className="text-xs text-slate-500">
                    {seriesActionModal.type === 'delete' ? 'Mantém o histórico passado' : 'Mantém o passado intacto'}
                  </p>
                </div>
              </Button>

              {/* Entire series */}
              <Button
                variant="outline"
                className={`w-full h-14 rounded-xl justify-start px-4 ${
                  seriesActionModal.type === 'delete' 
                    ? 'hover:border-rose-400 hover:bg-rose-50' 
                    : 'hover:border-violet-400 hover:bg-violet-50'
                }`}
                onClick={() => handleSeriesAction('all')}
                disabled={isSaving}
                data-testid="series-action-all"
              >
                <div className={`w-10 h-10 rounded-lg ${
                  seriesActionModal.type === 'delete' ? 'bg-rose-100' : 'bg-violet-100'
                } flex items-center justify-center mr-3`}>
                  <Repeat className={`w-5 h-5 ${
                    seriesActionModal.type === 'delete' ? 'text-rose-600' : 'text-violet-600'
                  }`} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Toda a série</p>
                  <p className="text-xs text-slate-500">
                    {seriesActionModal.type === 'delete' 
                      ? 'Apaga passado e futuro também' 
                      : 'Altera passado e futuro também'}
                  </p>
                </div>
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              variant="ghost" 
              className="rounded-xl"
              onClick={() => setSeriesActionModal({ isOpen: false, type: null, transaction: null, pendingData: null })}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== DIVISION SECTION COMPONENT ====================

function DivisionSection({ form, setForm, connections, type }) {
  const navigate = useNavigate();
  // Cores estáticas para garantir que o Tailwind gere o CSS no build
  const theme = type === "income" 
    ? { 
        bg: "bg-emerald-100", 
        text: "text-emerald-700", 
        border: "border-emerald-300", 
        ring: "border-emerald-500", 
        bgLight: "bg-emerald-50",
        textLight: "text-emerald-600",
        textDark: "text-emerald-800",
        borderLight: "border-emerald-100"
      }
    : { 
        bg: "bg-rose-100", 
        text: "text-rose-700", 
        border: "border-rose-300", 
        ring: "border-rose-500", 
        bgLight: "bg-rose-50",
        textLight: "text-rose-600",
        textDark: "text-rose-800",
        borderLight: "border-rose-100"
      };

  const amount = parseFloat(form.amount) || 0;
  
  const summary = (() => {
    if (form.selectedParticipants.length === 0 || amount === 0) return null;
    let myShare = 0; let others = [];
    if (form.divisionType === "equal") {
      const totalParticipants = form.allocateToOthers ? form.selectedParticipants.length : form.selectedParticipants.length + 1;
      const shareEach = amount / totalParticipants;
      myShare = form.allocateToOthers ? 0 : shareEach;
      form.selectedParticipants.forEach(pId => {
        const person = connections.people.find(p => p.id === pId);
        others.push({ name: person?.name, share: shareEach });
      });
    } else {
      let totalOthers = 0;
      form.selectedParticipants.forEach(pId => {
        const person = connections.people.find(p => p.id === pId);
        const share = parseFloat(form.participantShares[pId]) || 0;
        totalOthers += share;
        others.push({ name: person?.name, share: share });
      });
      myShare = form.allocateToOthers ? 0 : Math.max(0, amount - totalOthers);
    }
    return { myShare, others };
  })();

  return (
    
    <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
      {/* Select Participants */}
      <div className="space-y-2">
        <Label className="text-sm">Selecionar pessoas</Label>
      <div className="flex flex-wrap gap-2">
  {connections.people.length > 0 ? (
    connections.people.map(person => (
      <button
        key={person.id}
        type="button"
        onClick={() => {
          const isSelected = form.selectedParticipants.includes(person.id);
          setForm({
            ...form,
            selectedParticipants: isSelected
              ? form.selectedParticipants.filter(id => id !== person.id)
              : [...form.selectedParticipants, person.id]
          });
        }}
        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
          form.selectedParticipants.includes(person.id)
            ? `${theme.bg} ${theme.text} border-2 ${theme.border}`
            : "bg-white text-slate-600 border-2 border-slate-200"
        }`}
      >
        <span>{person.avatar}</span>
        <span>{person.name}</span>
        {form.selectedParticipants.includes(person.id) && <Check className="w-4 h-4" />}
      </button>
    ))
  ) : (
    <div className="w-full p-4 border-2 border-dashed border-slate-200 rounded-2xl text-center bg-white/50">
      <p className="text-xs text-slate-500 mb-2">Você ainda não adicionou pessoas para dividir contas.</p>
      <Button 
        variant="ghost" 
        size="sm" 
        className="h-8 text-[10px] text-indigo-600 hover:bg-indigo-50"
        onClick={() => navigate("/connections")}
      >
        + Adicionar Pessoa em Conexões
      </Button>
    </div>
  )}
</div>
      </div>

      {form.selectedParticipants.length > 0 && (
        <>
          {/* Allocate to others */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-slate-600">Alocar 100% aos outros</span>
            <Checkbox
              checked={form.allocateToOthers}
              onCheckedChange={(checked) => setForm({ ...form, allocateToOthers: checked })}
            />
          </div>

          {/* Division Type */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, divisionType: "equal" })}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                form.divisionType === "equal"
                  ? `${theme.ring} ${theme.bgLight} ${theme.text}`
                  : "border-slate-200 text-slate-600"
              }`}
            >
              <span className="text-sm font-medium">Dividir Igual</span>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, divisionType: "manual" })}
              className={`p-3 rounded-xl border-2 text-center transition-all ${
                form.divisionType === "manual"
                  ? `${theme.ring} ${theme.bgLight} ${theme.text}`
                  : "border-slate-200 text-slate-600"
              }`}
            >
              <span className="text-sm font-medium">Valor Manual</span>
            </button>
          </div>

          {/* Manual inputs */}
          {form.divisionType === "manual" && (
            <div className="space-y-2">
              {form.selectedParticipants.map(pId => {
                const person = connections.people.find(p => p.id === pId);
                return (
                  <div key={pId} className="flex items-center gap-3">
                    <span className="w-8 text-center">{person?.avatar}</span>
                    <span className="flex-1 text-sm font-medium">{person?.name}</span>
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">R$</span>
                      <Input
                        type="number"
                        placeholder="0,00"
                        value={form.participantShares[pId] || ""}
                        onChange={(e) => setForm({
                          ...form,
                          participantShares: { ...form.participantShares, [pId]: e.target.value }
                        })}
                        className="rounded-xl h-10 pl-10 text-right"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {summary && (
            <div className={`mt-4 ${theme.bgLight} rounded-xl p-4 border ${theme.borderLight}`}>
                <div className={`flex items-center gap-2 ${theme.text} font-medium mb-3`}>
                  <Sparkles className="w-4 h-4" />
                  <span>Resumo da divisão</span>
                </div>
                <div className="space-y-1.5">
                  {!form.allocateToOthers && (
                    <p className={`text-sm ${theme.textDark} flex justify-between`}>
                      <span><strong>Você</strong> (sua parte)</span>
                      <span>R$ {formatCurrency(summary.myShare)}</span>
                    </p>
                  )}
                  {summary.others.map((person, idx) => (
                    <p key={idx} className={`text-sm ${theme.textLight} flex justify-between`}>
                    <span><strong>{person.name}</strong></span>
                    <span>R$ {formatCurrency(person.share)}</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
